import { z } from 'zod';
import { uuidSchema, shortString, mediumString, positiveInt, nonNegativeInt, } from './validation.js';
/**
 * Warehouse Zones Schemas (BLU-345 Sprint 1)
 *
 * Zones subdivide a location into physical areas (picking, overstock,
 * receiving, returns, custom). Zone stock is an overlay on sku_inventory —
 * the location-level on-hand stays the source of truth; the difference is
 * surfaced as "unzoned" by the reconciliation query.
 */
export const zoneKindSchema = z.enum([
    'picking',
    'overstock',
    'receiving',
    'returns',
    'custom',
]);
export const restockTaskStatusSchema = z.enum(['OPEN', 'COMPLETED', 'CANCELLED']);
// ---- Zone CRUD ----
export const createZoneSchema = z.object({
    locationId: uuidSchema,
    name: shortString.min(1, 'Zone name is required'),
    zoneKind: zoneKindSchema,
    isDefaultReceiving: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
    color: shortString.optional(),
    description: mediumString.optional(),
});
export const updateZoneSchema = z.object({
    zoneId: uuidSchema,
    name: shortString.min(1).optional(),
    zoneKind: zoneKindSchema.optional(),
    isDefaultReceiving: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
    color: shortString.nullable().optional(),
    description: mediumString.nullable().optional(),
});
export const zoneIdSchema = z.object({
    zoneId: uuidSchema,
});
export const listZonesSchema = z.object({
    locationId: uuidSchema,
    includeArchived: z.boolean().optional(),
});
// ---- Zone inventory ----
export const listZoneInventorySchema = z.object({
    locationId: uuidSchema,
    zoneId: uuidSchema.optional(),
});
export const getSkuZoneBreakdownSchema = z.object({
    locationId: uuidSchema,
    skuId: uuidSchema,
});
export const getZoneReconciliationSchema = z.object({
    locationId: uuidSchema,
});
/**
 * Move stock between zones (or adopt from / release to the unzoned pool).
 * At least one of fromZoneId / toZoneId must be provided.
 */
export const moveZoneStockSchema = z
    .object({
    locationId: uuidSchema,
    skuId: uuidSchema,
    fromZoneId: uuidSchema.optional(),
    toZoneId: uuidSchema.optional(),
    quantity: positiveInt,
    notes: mediumString.optional(),
})
    .refine((v) => v.fromZoneId != null || v.toZoneId != null, {
    message: 'At least one of fromZoneId or toZoneId is required',
})
    .refine((v) => v.fromZoneId !== v.toZoneId, {
    message: 'Source and destination zones must differ',
});
/**
 * Set-style correction of a zone quantity. Notes are REQUIRED — every
 * unexplained stock "set" is a future stocktake mystery (BLU-617).
 */
export const adjustZoneStockSchema = z.object({
    locationId: uuidSchema,
    zoneId: uuidSchema,
    skuId: uuidSchema,
    newQuantity: nonNegativeInt,
    notes: mediumString.min(1, 'A reason is required for zone adjustments'),
});
export const setZoneThresholdsSchema = z
    .object({
    zoneId: uuidSchema,
    skuId: uuidSchema,
    restockMinQty: nonNegativeInt.nullable(),
    restockTargetQty: nonNegativeInt.nullable(),
})
    .refine((v) => v.restockTargetQty == null || v.restockMinQty != null, {
    message: 'A target quantity requires a minimum quantity',
});
// ---- Restock tasks ----
export const listRestockTasksSchema = z.object({
    locationId: uuidSchema,
    status: restockTaskStatusSchema.optional(),
});
export const createRestockTaskSchema = z.object({
    locationId: uuidSchema,
    skuId: uuidSchema,
    fromZoneId: uuidSchema,
    toZoneId: uuidSchema,
    requestedQuantity: positiveInt,
    notes: mediumString.optional(),
});
/**
 * Complete a restock task. movedQuantity may be less than requested
 * (partial completion → auto follow-up task) or 0 (nothing available →
 * task is cancelled).
 */
export const completeRestockTaskSchema = z.object({
    taskId: uuidSchema,
    movedQuantity: nonNegativeInt,
});
export const cancelRestockTaskSchema = z.object({
    taskId: uuidSchema,
    notes: mediumString.optional(),
});
